<template>
    <span>
        <span @click="startConfirming">
            <slot />
        </span>

        <Dialog :open="confirming" @update:open="closeModal">
            <DialogContent class="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{{ title }}</DialogTitle>
                </DialogHeader>

                <p class="text-sm text-muted-foreground">
                    {{ content }}
                </p>

                <div>
                    <Label for="confirm-password-input">Password</Label>
                    <Input
                        id="confirm-password-input"
                        v-model="password"
                        type="password"
                        placeholder="Enter your password"
                        class="mt-2"
                        @keydown.enter="confirmPassword"
                    />
                    <p v-if="error" class="mt-1 text-xs text-destructive">{{ error }}</p>
                </div>

                <DialogFooter class="mt-4">
                    <Button variant="outline" @click="closeModal">Cancel</Button>
                    <Button :disabled="!password || processing" @click="confirmPassword">
                        {{ button }}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </span>
</template>

<script lang="ts" setup>
import { ref, nextTick } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const props = withDefaults(defineProps<{
    title?: string;
    content?: string;
    button?: string;
    forceConfirmation?: boolean;
    skipConfirmation?: boolean;
}>(), {
    title: 'Confirm Password',
    content: 'For your security, please confirm your password to continue.',
    button: 'Confirm',
    forceConfirmation: false,
    skipConfirmation: false,
});

const emit = defineEmits<{
    confirmed: [password: string];
}>();

const confirming = ref(false);
const processing = ref(false);
const password = ref('');
const error = ref('');

const startConfirming = () => {
    if (props.forceConfirmation || props.skipConfirmation) {
        confirming.value = true;
        return;
    }

    axios.get(route('password.confirmation')).then((response) => {
        if (response.data.confirmed) {
            emit('confirmed', '');
        } else {
            confirming.value = true;
        }
    });
};

const confirmPassword = () => {
    if (!password.value) return;

    if (props.skipConfirmation) {
        const pw = password.value;
        closeModal();
        nextTick(() => emit('confirmed', pw));
        return;
    }

    processing.value = true;
    error.value = '';

    const pw = password.value;
    axios
        .post(route('password.confirm'), { password: pw })
        .then(() => {
            processing.value = false;
            closeModal();
            nextTick(() => emit('confirmed', pw));
        })
        .catch((err) => {
            processing.value = false;
            error.value = err.response?.data?.errors?.password?.[0] || 'Incorrect password';
        });
};

const closeModal = () => {
    confirming.value = false;
    password.value = '';
    error.value = '';
};
</script>
